import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { offlineStorage, OfflineTimeEntry } from '@/lib/offline-storage';
import { useOfflineStatus } from './useOfflineStatus';
import { useToast } from './use-toast';

const SYNC_INTERVAL = 30000; // 30 seconds

export function useOfflineSync() {
  const { isOnline } = useOfflineStatus();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Update pending count
  const updatePendingCount = useCallback(async () => {
    const count = await offlineStorage.getPendingCount();
    setPendingCount(count);
  }, []);

  // Sync a single entry
  const syncEntry = async (entry: OfflineTimeEntry): Promise<boolean> => {
    try {
      // Check if entry already exists (by local_id in metadata or by start_time match)
      const { data: existing } = await supabase
        .from('time_entries')
        .select('id')
        .eq('user_id', entry.user_id)
        .eq('start_time', entry.start_time)
        .maybeSingle();

      if (existing) {
        // Already synced, just mark as synced locally
        return true;
      }

      // Insert new entry
      const { error } = await supabase.from('time_entries').insert({
        id: entry.id,
        user_id: entry.user_id,
        project_id: entry.project_id,
        category: entry.category as any,
        custom_category: entry.custom_category,
        description: entry.description,
        start_time: entry.start_time,
        end_time: entry.end_time,
        duration_minutes: entry.duration_minutes,
        is_billable: entry.is_billable,
        hourly_rate: entry.hourly_rate,
        is_paused: entry.is_paused,
        paused_duration_seconds: entry.paused_duration_seconds,
      });

      if (error) {
        console.error('Sync error for entry:', entry.local_id, error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Sync exception for entry:', entry.local_id, error);
      return false;
    }
  };

  // Main sync function
  const syncOfflineEntries = useCallback(async () => {
    if (!isOnline || isSyncing) return;

    const unsyncedEntries = await offlineStorage.getUnsyncedEntries();
    if (unsyncedEntries.length === 0) return;

    setIsSyncing(true);

    try {
      const syncedLocalIds: string[] = [];
      let successCount = 0;
      let failCount = 0;

      for (const entry of unsyncedEntries) {
        const success = await syncEntry(entry);
        if (success) {
          syncedLocalIds.push(entry.local_id);
          successCount++;
        } else {
          failCount++;
        }
      }

      // Mark synced entries
      if (syncedLocalIds.length > 0) {
        await offlineStorage.markAsSynced(syncedLocalIds);
        await updatePendingCount();
        
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['time_entries'] });

        if (successCount > 0) {
          toast({
            title: 'Sincronização concluída',
            description: `${successCount} entrada(s) sincronizada(s) com sucesso.`,
          });
        }
      }

      if (failCount > 0) {
        toast({
          variant: 'destructive',
          title: 'Erro na sincronização',
          description: `${failCount} entrada(s) não puderam ser sincronizadas. Tentaremos novamente.`,
        });
      }

      setLastSyncTime(new Date());

      // Cleanup synced entries older than 7 days
      await offlineStorage.cleanupSyncedEntries();
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isSyncing, queryClient, toast, updatePendingCount]);

  // Initial load
  useEffect(() => {
    updatePendingCount();
  }, [updatePendingCount]);

  // Sync when coming back online
  useEffect(() => {
    const handleBackOnline = () => {
      syncOfflineEntries();
    };

    window.addEventListener('app:back-online', handleBackOnline);
    return () => window.removeEventListener('app:back-online', handleBackOnline);
  }, [syncOfflineEntries]);

  // Periodic sync when online
  useEffect(() => {
    if (isOnline) {
      // Initial sync
      syncOfflineEntries();

      // Set up interval
      syncIntervalRef.current = setInterval(syncOfflineEntries, SYNC_INTERVAL);
    }

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [isOnline, syncOfflineEntries]);

  return {
    pendingCount,
    isSyncing,
    lastSyncTime,
    syncNow: syncOfflineEntries,
    updatePendingCount,
  };
}
